// app-subdomain-creators/src/stores/appSettingsStore.js
import { defineStore } from 'pinia';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

export const useAppSettingsStore = defineStore('appSettings', {
  state: () => ({
    settings: null,
    logo: null,
    favicon: null, 
    isLoaded: false, 
  }),

  actions: {
    // ============================================================
    // GET SETTINGS
    // ============================================================
    async fetchSettings() {
      const url = `${apiProxyUrl}/api/app-settings`;
      console.log("🔵 [STORE] GET /app-settings →", url);

      try {
        const res = await fetch(url, {
          credentials: 'include',
        });

        console.log("📥 [STORE] RESPONSE STATUS:", res.status);

        let data;
        try {
          data = await res.json();
        } catch (err) {
          console.error("❌ [STORE] res.json() FAILED:", err);
          return;
        }

        console.log("📥 [STORE] SETTINGS JSON FROM SERVER:", data);

        if (!res.ok) {
          console.error("❌ [STORE] GET /app-settings returned error", res.status, data);
          return;
        }

        this.settings = data;
        console.log("✅ [STORE] SETTINGS SAVED IN STORE:", this.settings);

        // ⭐ LOAD LOGO + FAVICON
        await this.fetchLogo();
        await this.fetchFavicon();   // ⭐ ADDED

        this.isLoaded = true;   // ⭐ MARK STORE AS READY

      } catch (err) {
        console.error("💥 [STORE] fetchSettings CRASHED:", err);
      }
    },

    // ============================================================
    // ⭐ NEW — FETCH LOGO STREAMING URL WITH CACHE BUSTING
    // ============================================================
    async fetchLogo() {
      try {
        this.logo = `${apiProxyUrl}/api/main-app-logo`;
        console.log("🟢 [STORE] NEW LOGO URL:", this.logo);
      } catch (e) {
        console.error("❌ Logo fetch failed", e);
      }
    },

    // ============================================================
    // ⭐ FETCH FAVICON STREAMING URL WITH CACHE BUSTING
    // ============================================================
    async fetchFavicon() {
      try {
        this.favicon = `${apiProxyUrl}/api/main-app-favicon?cb=${Date.now()}`;
        console.log("🟣 [STORE] Favicon URL:", this.favicon);
      } catch (e) {
        console.error("❌ Favicon fetch failed", e);
      }
    },

    // ============================================================
    // SAVE SETTINGS (global app settings from Theme Editor)
    // payload shape from saveGlobalAppSettings():
    // {
    //   membershipName,
    //   seoTitle,
    //   seoDescription,
    //   navBgColor,
    //   textIconColor,
    //   textIconHoverColor,
    //   productHoverColor,
    // }
    // ============================================================
    async saveSettings(payload) {
  console.log("🟡 [STORE] saveSettings(payload):", payload);

  const body = {
    nav_bg_color: payload.navBgColor,
    text_icon_color: payload.textIconColor,
    text_icon_hover_color: payload.textIconHoverColor,
    product_hover_color: payload.productHoverColor,
    membership_name: typeof payload.membershipName === "string"
      ? payload.membershipName
      : (this.settings?.membership_name ?? null),
    seo_title: payload.seoTitle,
    seo_description: payload.seoDescription,

    // KEEP current logo/favicons
    logo_url: this.settings?.logo_url ?? null,
    favicon_url: this.settings?.favicon_url ?? null,
  };

  console.log("📝 PUT BODY:", body);

  const res = await fetch(`${apiProxyUrl}/api/app-settings`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  let data = null;
  try {
    data = await res.json();
  } catch (err) {
    console.log("❌ saveSettings json fail", err);
  }

  console.log("📥 saveSettings response:", data);

  if (!res.ok) {
    const message = data?.message || data?.error || "Unable to save global app settings.";
    console.log("❌ saveSettings backend failed");
    throw new Error(message);
  }

  // ⭐⭐⭐ THE MOST IMPORTANT PART ⭐⭐⭐
  // MERGE new values back into store
  this.settings = {
    ...(this.settings || {}),
    nav_bg_color: body.nav_bg_color,
    text_icon_color: body.text_icon_color,
    text_icon_hover_color: body.text_icon_hover_color,
    product_hover_color: body.product_hover_color,
    membership_name: body.membership_name,
    seo_title: body.seo_title,
    seo_description: body.seo_description,
    logo_url: this.settings?.logo_url ?? null,
    favicon_url: this.settings?.favicon_url ?? null,
    updated_at: new Date().toISOString(),
  };

  console.log("🔥 UPDATED STORE SETTINGS:", this.settings);
  return data;
}
,

    // ============================================================
    // UPLOAD LOGO (unchanged except we also keep store in sync)
    // ============================================================
async uploadLogo(file) {
  console.log("🟣 [STORE] LOGO UPLOAD START");
  console.log("📝 File:", { name: file.name, type: file.type, size: file.size });

  //
  // 1) Request signed upload URL (PROXY ROUTE)
  //
  console.log("➡️  [STORE] POST /generate-logo-upload-url");

  const res1 = await fetch(`${apiProxyUrl}/api/generate-logo-upload-url`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type,
    }),
  });

  let signedData = null;
  try {
    signedData = await res1.json();
  } catch (err) {
    console.log("❌ Failed to parse signed URL JSON:", err);
  }

  console.log("📥 [STORE] SIGNED URL RESPONSE:", signedData);

  if (!res1.ok || !signedData) {
    console.error("❌ Failed to get signed URL");
    return;
  }

  const uploadUrl = signedData.uploadUrl || signedData.signedUrl;
  const finalUrl  = signedData.finalUrl  || signedData.fileUrl;

  console.log("🔗 uploadUrl:", uploadUrl);
  console.log("🔗 finalUrl:", finalUrl);

  //
  // 2) Upload the file directly to Exoscale
  //
  console.log("⬆️  [STORE] PUT → Exoscale");
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  console.log("📥 [STORE] PUT STATUS:", putRes.status);

  //
  // 3) Save URL to backend (PROXY ROUTE)
  //
  console.log("🟢 [STORE] POST /logo");

  const res2 = await fetch(`${apiProxyUrl}/api/logo`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ logo_url: finalUrl }),
  });

  let saveData = null;
  try {
    saveData = await res2.json();
  } catch (err) {
    console.log("❌ Failed to parse logo save JSON:", err);
  }

  console.log("📥 [STORE] SAVE LOGO RESPONSE:", saveData);

  if (!this.settings) this.settings = {};
  this.settings.logo_url = saveData?.logo_url || finalUrl;

  console.log("✅ [STORE] UPDATED settings.logo_url:", this.settings.logo_url);
},




  },
});
